/**
 * DashboardStatsCard - Compact horizontal stats card
 * WCAG 2.1 Level AA + BFSG konform | Dark-mode-first
 */

import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

interface DashboardStatsCardProps {
  id?: string;
  label: string;
  value: string | number;
  suffix?: string;
  icon: React.ElementType;
  iconColor: string;
  iconBgColor: string;
  trend?: { value: string; positive: boolean };
  children?: React.ReactNode;
}

export function DashboardStatsCard({
  id,
  label,
  value,
  suffix,
  icon: Icon,
  iconColor,
  iconBgColor,
  trend,
  children,
}: DashboardStatsCardProps) {
  return (
    <div id={id} className="dashboard-stats-card flex items-center gap-3 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800/80 px-4 py-3">
      <div className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg border ${iconBgColor}`}>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wider text-neutral-500">{label}</p>
        <div className="flex items-baseline gap-1.5">
          <span className="text-xl font-bold tabular-nums text-neutral-900 dark:text-white">{value}</span>
          {suffix && <span className="text-sm text-neutral-400">{suffix}</span>}
        </div>
        {trend && (
          <span
            className={`mt-0.5 flex items-center gap-1 text-xs font-medium ${
              trend.positive ? 'text-emerald-500' : 'text-red-500'
            }`}
          >
            {trend.positive ? (
              <ArrowUpRight className="h-3.5 w-3.5" />
            ) : (
              <ArrowDownRight className="h-3.5 w-3.5" />
            )}
            {trend.value}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
